/*
 * Product page behaviour.
 *
 * <product-page> reacts to variant picker changes: it sends the checked
 * option value IDs to the server (`option_values`), swaps the variant state
 * and the price, picker, availability, and add-to-cart partials, then syncs
 * the form's variant ID, the URL, the gallery, and the SKU in the details. An
 * add while that answer is on its way waits for it, so the form never posts
 * the variant from before the change. When the cart
 * drawer is on the page, it opens the drawer with the line already in it
 * (from the page's own data), adds through /cart/add.js, and stays on the
 * page; a failed add takes the line back out.
 *
 * <product-gallery> drives the media slider: previous/next buttons (wrapping
 * around), arrow keys, mouse drag on desktop (touch swipes natively), and
 * thumbnails. An IntersectionObserver tracks the visible slide to update the
 * counter and the active thumbnail and pause videos that scroll away; the
 * slides' images are the browser's own lazy images. Clicking an image opens
 * <product-zoom> at that image; closing it returns the slider to the image
 * last viewed. Animation is skipped when the shopper prefers reduced motion.
 *
 * <product-zoom> is the full-screen viewer (see its class below).
 */

const VARIANT_PARTIALS = ['product-variant', 'product-price', 'variant-picker', 'product-availability', 'product-delivery', 'product-add'];

// The chosen value of each option: a checked radio, or the selected entry of a colour dropdown
// (snippets/variant-picker.liquid). Both carry the value's name and its option value ID.
const CHOSEN_VALUES = '[data-variant-picker] :is(input[type="radio"]:checked, option:checked)';

class ProductPage extends HTMLElement {
  #abort;
  #request;
  // The latest option change while the server's answer is on its way (#updateVariant's promise).
  #update = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#request?.abort();
  }

  // What the page already knows about the variant being added, for the cart drawer's line.
  #pendingLine(form) {
    const state = this.querySelector('[data-variant-state]');
    const variantId = form.querySelector('[name="id"]')?.value;
    const mediaId = state?.dataset.mediaId;
    const image =
      (mediaId && this.querySelector(`.product-gallery__slide[data-media-id="${CSS.escape(mediaId)}"] img`)) ||
      this.querySelector('.product-gallery__slide img');
    const options = [...this.querySelectorAll(CHOSEN_VALUES)].map((choice) => choice.value);

    return {
      variantId,
      productId: form.querySelector('[name="product-id"]')?.value,
      quantity: Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1),
      title: this.querySelector('.product__title')?.textContent.trim() ?? '',
      variantTitle: options.join(' / '),
      image: image?.currentSrc || image?.src,
      unitPrice: state?.dataset.price ? Number(state.dataset.price) : undefined,
      url: `${window.location.pathname}?variant=${variantId}`,
    };
  }

  #onChange = (event) => {
    const field = event.target;
    if (!field.matches('[data-variant-picker] :is(input[type="radio"], select)')) return;
    const choice = field.matches('select') ? field.selectedOptions[0] : field;

    // Answer the click at once (the picked name, the variant's image) while the server resolves the rest.
    const selected = field.closest('fieldset')?.querySelector('.variant-picker__selected');
    if (selected) selected.textContent = choice.value;
    if (choice.dataset.mediaId) this.querySelector('product-gallery')?.showMedia(choice.dataset.mediaId);

    const update = this.#updateVariant();
    this.#update = update;
    const done = () => {
      if (this.#update === update) this.#update = null;
    };
    update.then(done, done);
  };

  // Resolves once no option change waits for the server: true when the page shows the chosen
  // variant, false when it's loading the full page for it instead.
  async #settled() {
    let synced = true;
    for (let update = this.#update; update; update = this.#update) {
      synced = await update.catch(() => false);
      if (this.#update === update) break;
    }
    return synced !== false;
  }

  #onSubmit = async (event) => {
    const form = event.target;
    if (!form.matches('.product__form')) return;
    const drawer = document.querySelector('cart-drawer');
    const withDrawer = typeof drawer?.refresh === 'function';
    // Without the drawer the browser posts the form itself, unless an option change is on its way.
    if (!withDrawer && !this.#update) return;

    event.preventDefault();
    // Before the wait: the answer replaces the add buttons, the clicked one too.
    const fromStickyBar = Boolean(event.submitter) && !form.contains(event.submitter);

    // An add right after picking an option waits for the server's answer (the add button is dimmed
    // meanwhile), so the form posts the variant the page now shows; Horizon holds the add the same way.
    if (this.#update) {
      const synced = await this.#settled();
      if (!synced || form.querySelector('[name="add"]')?.disabled) return;
    }
    if (!withDrawer) {
      form.submit();
      return;
    }

    // The form's own button and the sticky bar's (it submits from outside the form).
    const buttons = [...this.querySelectorAll('[name="add"]')].filter((button) => button.form === form);
    const error = this.querySelector('[data-form-error]');
    error.hidden = true;
    buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));

    // Open the drawer with the line already in it, so the click answers immediately; /cart/add.js's
    // answer then fills in the exact price and key, and the refresh brings the totals.
    const formData = new FormData(form);
    drawer.open();
    drawer.setBusy?.(true);
    const pending = drawer.addPending?.(this.#pendingLine(form));

    try {
      const response = await fetch(`${form.action}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        drawer.revertPending?.(pending);
        drawer.close();
        error.textContent = body.description || error.dataset.fallback;
        error.hidden = false;
        // Added from the sticky bar: the message is by the form, out of view, so bring it in.
        if (fromStickyBar) error.scrollIntoView({ block: 'center' });
        return;
      }

      drawer.confirmPending?.(pending, body.items ?? [body]);
      await drawer.refresh();
    } catch {
      // Network or runtime failure: fall back to the regular form post.
      form.submit();
    } finally {
      drawer.setBusy?.(false);
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
    }
  };

  // Resolves to true once the page shows the chosen options' variant, to false when it's loading the
  // full page for them instead, and to nothing when a later change took over.
  async #updateVariant() {
    const optionValueIds = [...this.querySelectorAll(CHOSEN_VALUES)].map((choice) => choice.dataset.optionValueId);

    // Start from the current URL to keep locale, market, and collection context. Shopify renders only
    // this section for the chosen options (Section Rendering API, `section_id`), not the whole page.
    const url = new URL(window.location.href);
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', optionValueIds.join(','));
    url.searchParams.set('section_id', this.dataset.sectionId);

    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.setAttribute('aria-busy', 'true');

    try {
      // Without the variant state (assets/regions.js) in the answer, the form would keep posting the
      // previous variant: fall back to a full page load (also when no region came back at all).
      const regions = await window.themeRegions.load(VARIANT_PARTIALS, { url: url.toString(), signal: request.signal });
      if (!regions.has('product-variant')) throw new Error('No variant state');
      window.themeRegions.apply(regions);
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Fall back to loading the full page for these options.
      url.searchParams.delete('section_id');
      window.location.assign(url);
      return false;
    } finally {
      if (this.#request === request) this.removeAttribute('aria-busy');
    }

    this.#syncVariant();
    return true;
  }

  #syncVariant() {
    const state = this.querySelector('[data-variant-state]');
    if (!state) return;

    const { variantId, mediaId, sku } = state.dataset;

    // The variant ID input lives outside the regions (the form posts it), so it's set here: empty for
    // an option combination that doesn't exist (its add button is disabled), as in Horizon.
    const idInput = this.querySelector('[data-variant-id-input]');
    if (idInput) {
      idInput.value = variantId ?? '';
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('option_values');
    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }
    window.history.replaceState(window.history.state, '', url);

    if (mediaId) this.querySelector('product-gallery')?.showMedia(mediaId);

    // Widgets that follow the price (payment-options.js: Svea's calculator) listen for this.
    this.dispatchEvent(new CustomEvent('product:variant-change', { bubbles: true, detail: { variantId, price: state.dataset.price } }));

    // The SKU row lives in the product details band, outside this element.
    for (const row of document.querySelectorAll('[data-sku-row]')) {
      row.hidden = !sku;
      const value = row.querySelector('[data-sku-value]');
      if (value) value.textContent = sku ?? '';
    }
  }
}

// A mouse drag shorter than this is a click; one longer than this share of the slide width (or a fast flick) changes slide.
const DRAG_START_DISTANCE = 6;
const DRAG_SLIDE_SHARE = 0.15;
const FLICK_VELOCITY = 0.4; // px per ms

class ProductGallery extends HTMLElement {
  #abort;
  #observer;
  #index = -1;
  #target = null;
  #dragged = false;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.slides = this.querySelector('[data-gallery-slides]');
    if (!this.slides) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.items = [...this.slides.children];
    this.thumbs = [...this.querySelectorAll('.product-gallery__thumb')];
    this.thumbList = this.querySelector('.product-gallery__thumbs');
    this.counter = this.querySelector('[data-gallery-current]');
    this.zoom = this.querySelector('product-zoom');

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId), { signal });
    });
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.#step(1), { signal });
    this.slides.addEventListener('keydown', this.#onKeydown, { signal });
    this.slides.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.slides.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    // Capture phase, so the click that ends a drag never reaches the zoom button.
    this.slides.addEventListener('click', this.#onSlidesClick, { signal, capture: true });

    // Back from the viewer, the slider shows the image last viewed there.
    this.addEventListener('product-zoom:close', (event) => this.showMedia(event.detail.mediaId, 'instant'), { signal });

    // Slides are full width, so at most one is 60 % visible: that's the active one. While a
    // button, key, or thumbnail scrolls to a slide, the slides it passes don't count.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = this.items.indexOf(entry.target);
          if (!entry.isIntersecting || (this.#target !== null && index !== this.#target)) continue;
          this.#target = null;
          this.#setActive(index);
        }
      },
      { root: this.slides, threshold: 0.6 }
    );
    // A shopper taking over ends that. (Not `scrollend`: an interrupted smooth scroll fires it too.)
    for (const type of ['pointerdown', 'touchstart', 'wheel']) {
      this.slides.addEventListener(type, () => (this.#target = null), { signal, passive: true });
    }
    this.items.forEach((item) => this.#observer.observe(item));

    const selected = this.#indexOf(this.dataset.selectedMedia);
    if (selected > 0) this.#scrollTo(selected, 'instant');
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  get #behavior() {
    return this.#reducedMotion.matches ? 'instant' : 'smooth';
  }

  showMedia(mediaId, behavior = this.#behavior) {
    const index = this.#indexOf(mediaId);
    if (index !== -1) this.#scrollTo(index, behavior);
  }

  #indexOf(mediaId) {
    return this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
  }

  #scrollTo(index, behavior = this.#behavior) {
    const slide = this.items[index];
    if (!slide) return;

    if (Math.abs(this.slides.scrollLeft - slide.offsetLeft) > 1) this.#target = index;
    this.slides.scrollTo({ left: slide.offsetLeft, behavior });
    this.#setActive(index);
  }

  // Previous/next wrap around at the ends.
  #step(direction) {
    const count = this.items.length;
    this.#scrollTo((Math.max(this.#index, 0) + direction + count) % count);
  }

  #setActive(index) {
    if (index === this.#index || index < 0) return;
    this.#index = index;

    if (this.counter) this.counter.textContent = String(index + 1);

    const mediaId = this.items[index].dataset.mediaId;
    for (const thumb of this.thumbs) {
      if (thumb.dataset.mediaId === mediaId) {
        thumb.setAttribute('aria-current', 'true');
        this.#centerThumb(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    }

    this.items.forEach((item, itemIndex) => {
      if (itemIndex !== index) item.querySelector('video')?.pause();
    });
  }

  // Scrolls only the thumbnail strip, a row or (desktop, thumbnails on the left) a column;
  // scrollIntoView() could scroll the page too.
  #centerThumb(thumb) {
    const list = this.thumbList;
    if (!list) return;
    const item = thumb.parentElement;

    if (list.scrollHeight > list.clientHeight + 1) {
      list.scrollTo({ top: item.offsetTop - (list.clientHeight - item.offsetHeight) / 2, behavior: this.#behavior });
    } else if (list.scrollWidth > list.clientWidth + 1) {
      list.scrollTo({ left: item.offsetLeft - (list.clientWidth - item.offsetWidth) / 2, behavior: this.#behavior });
    }
  }

  #onKeydown = (event) => {
    const steps = { ArrowLeft: -1, ArrowRight: 1 };
    if (event.key in steps) {
      event.preventDefault();
      this.#step(steps[event.key]);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.#scrollTo(event.key === 'Home' ? 0 : this.items.length - 1);
    }
  };

  // Mouse drag on desktop; touch and pens scroll the slider natively.
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    // A press on an image is most likely a click that opens the viewer: start loading its copy now.
    const zoomButton = event.target.closest('[data-zoom-media]');
    if (zoomButton) this.zoom?.prefetch(zoomButton.dataset.zoomMedia);
    if (this.items.length < 2) return;
    if (event.target.closest('video, iframe, model-viewer')) return;

    const startX = event.clientX;
    const startScroll = this.slides.scrollLeft;
    const startIndex = this.#index;
    let last = { x: startX, time: event.timeStamp };
    let velocity = 0;
    let dragging = false;

    const onMove = (moveEvent) => {
      const distance = moveEvent.clientX - startX;
      if (!dragging) {
        if (Math.abs(distance) < DRAG_START_DISTANCE) return;
        dragging = true;
        this.slides.setPointerCapture(event.pointerId);
        this.slides.classList.add('is-dragging');
      }

      const elapsed = moveEvent.timeStamp - last.time;
      if (elapsed > 0) velocity = (moveEvent.clientX - last.x) / elapsed;
      last = { x: moveEvent.clientX, time: moveEvent.timeStamp };
      this.slides.scrollLeft = startScroll - distance;
    };

    const onEnd = (endEvent) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onEnd);
      document.removeEventListener('pointercancel', onEnd);
      if (!dragging) return;

      // The click that ends this drag fires before any queued task, so the flag only lasts for it.
      this.#dragged = true;
      setTimeout(() => (this.#dragged = false));
      const distance = endEvent.clientX - startX;
      const passed = Math.abs(distance) > this.slides.clientWidth * DRAG_SLIDE_SHARE;
      const flicked = Math.abs(velocity) > FLICK_VELOCITY;
      const direction = passed || flicked ? -Math.sign(distance) : 0;
      const target = Math.min(Math.max(startIndex + direction, 0), this.items.length - 1);

      // Snapping stays off until the settle animation ends, or it would fight the scroll.
      const settle = () => this.slides.classList.remove('is-dragging');
      this.slides.addEventListener('scrollend', settle, { once: true });
      setTimeout(settle, 600);
      this.#scrollTo(target);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onEnd);
    document.addEventListener('pointercancel', onEnd);
  };

  #onSlidesClick = (event) => {
    if (this.#dragged) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const zoomButton = event.target.closest('[data-zoom-media]');
    if (zoomButton) this.zoom?.open(zoomButton.dataset.zoomMedia);
  };
}

/*
 * <product-zoom> is the gallery's full-screen viewer (snippets/product-gallery.liquid): the product's
 * images fitted to the screen in a swipeable snap row, with previous/next buttons, arrow keys,
 * thumbnails on wider screens, and a counter.
 *
 * - Zoom: a click or double tap zooms in (to the original's resolution, 2× to 3×) and again goes
 *   back; a mouse then pans by moving over the image, a finger by dragging (with a short glide).
 *   Pinching, or a trackpad pinch (ctrl + wheel), zooms freely. Only the open image is transformed,
 *   so zooming never lays out the page; a zoomed image asks for a file sharp enough for its size.
 * - Speed: nothing loads while closed. A mouse press on a gallery image starts loading its viewer
 *   copy; opening shows the gallery's already loaded copy at once, under the sharp one that replaces
 *   it when it arrives; only the open image and its neighbours load.
 * - A click beside the image, the close button, or Escape closes it.
 */
const ZOOM_STEP_MIN = 2;
const ZOOM_STEP_MAX = 3;
const ZOOM_MAX = 4;
const DOUBLE_TAP_MS = 300;
const TAP_SLOP = 10; // px a finger may move and still tap

class ProductZoom extends HTMLElement {
  #abort;
  #observer;
  #index = -1;
  #target = null;
  #scale = 1;
  #x = 0;
  #y = 0;
  #frame = null;
  #gesture = null;
  #lastTap = null;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.list = this.querySelector('[data-zoom-slides]');
    if (!this.dialog || !this.list) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.slides = [...this.list.children];
    this.thumbs = [...this.querySelectorAll('[data-zoom-thumb]')];
    this.thumbList = this.querySelector('.product-zoom__thumbs');
    this.counter = this.querySelector('[data-zoom-current]');

    this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.dialog.close(), { signal });
    this.querySelector('[data-zoom-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('[data-zoom-next]')?.addEventListener('click', () => this.#step(1), { signal });
    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.#go(index), { signal }));
    this.dialog.addEventListener('keydown', this.#onKeydown, { signal });
    this.dialog.addEventListener('close', this.#onClose, { signal });
    this.list.addEventListener('click', this.#onClick, { signal });
    this.list.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.list.addEventListener('wheel', this.#onWheel, { signal, passive: false });
    this.list.addEventListener('touchstart', this.#onTouchStart, { signal, passive: false });
    this.list.addEventListener('touchmove', this.#onTouchMove, { signal, passive: false });
    this.list.addEventListener('touchend', this.#onTouchEnd, { signal });
    this.list.addEventListener('touchcancel', this.#onTouchEnd, { signal });
    this.list.addEventListener('transitionend', (event) => event.target.classList.remove('is-animating'), { signal });
    window.addEventListener('resize', () => this.dialog.open && this.#reset(), { signal });

    // As in the gallery: the slide 60 % in view is the open one, except those a button scrolls past.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = this.slides.indexOf(entry.target);
          if (!entry.isIntersecting || (this.#target !== null && index !== this.#target)) continue;
          this.#target = null;
          this.#setActive(index);
        }
      },
      { root: this.list, threshold: 0.6 }
    );
    for (const type of ['pointerdown', 'touchstart', 'wheel']) {
      this.list.addEventListener(type, () => (this.#target = null), { signal, passive: true });
    }
    this.slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  get #behavior() {
    return this.#reducedMotion.matches ? 'instant' : 'smooth';
  }

  get #image() {
    return this.slides[this.#index]?.querySelector('img');
  }

  #indexOf(mediaId) {
    return this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
  }

  // Starts loading an image's viewer copy before the viewer opens.
  prefetch(mediaId) {
    const image = this.slides[this.#indexOf(mediaId)]?.querySelector('img');
    if (image) image.loading = 'eager';
  }

  open(mediaId) {
    if (!this.dialog || this.dialog.open) return;
    const index = Math.max(this.#indexOf(mediaId), 0);
    this.#preview(index);
    this.dialog.showModal();
    this.list.scrollTo({ left: this.slides[index].offsetLeft, behavior: 'instant' });
    this.#setActive(index);
  }

  #onClose = () => {
    this.#reset();
    const mediaId = this.slides[this.#index]?.dataset.mediaId;
    this.dispatchEvent(new CustomEvent('product-zoom:close', { bubbles: true, detail: { mediaId } }));
  };

  // Shows the gallery's already loaded copy in an image's place until its own file arrives.
  #preview(index) {
    const image = this.slides[index]?.querySelector('img');
    if (!image || (image.complete && image.naturalWidth) || image.style.backgroundImage) return;
    const mediaId = CSS.escape(this.slides[index].dataset.mediaId);
    const copy = this.closest('product-gallery')?.querySelector(`.product-gallery__slide[data-media-id="${mediaId}"] img`);
    if (!copy?.complete || !copy.naturalWidth) return;
    image.style.backgroundImage = `url(${JSON.stringify(copy.currentSrc || copy.src)})`;
    image.addEventListener('load', () => (image.style.backgroundImage = ''), { once: true });
  }

  #step(direction) {
    const count = this.slides.length;
    this.#go((Math.max(this.#index, 0) + direction + count) % count);
  }

  #go(index) {
    const slide = this.slides[index];
    if (!slide) return;
    if (Math.abs(this.list.scrollLeft - slide.offsetLeft) > 1) this.#target = index;
    this.list.scrollTo({ left: slide.offsetLeft, behavior: this.#behavior });
    this.#setActive(index);
  }

  #setActive(index) {
    if (index === this.#index || index < 0) return;
    this.#reset();
    this.#index = index;
    if (this.counter) this.counter.textContent = String(index + 1);

    this.thumbs.forEach((thumb, thumbIndex) => {
      if (thumbIndex === index) {
        thumb.setAttribute('aria-current', 'true');
        this.#centerThumb(thumb.parentElement);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    // The open image and its neighbours load now, each over the gallery's copy where there is one.
    for (const neighbour of [index, index - 1, index + 1]) {
      const image = this.slides[neighbour]?.querySelector('img');
      if (!image) continue;
      this.#preview(neighbour);
      image.loading = 'eager';
    }
  }

  // Scrolls only the thumbnail strip; scrollIntoView() could move the dialog too.
  #centerThumb(item) {
    if (!this.thumbList || this.thumbList.scrollWidth <= this.thumbList.clientWidth) return;
    this.thumbList.scrollTo({
      left: item.offsetLeft - this.thumbList.offsetLeft - (this.thumbList.clientWidth - item.offsetWidth) / 2,
      behavior: this.#behavior,
    });
  }

  #onKeydown = (event) => {
    const steps = { ArrowLeft: -1, ArrowRight: 1 };
    if (event.key in steps) {
      event.preventDefault();
      this.#step(steps[event.key]);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.#go(event.key === 'Home' ? 0 : this.slides.length - 1);
    }
  };

  /* Zoom geometry: the image is centred in its slide, and its transform (origin top left) is
     translate(x, y) scale(s). The frame is measured once per zoom; a transform doesn't move layout. */

  #measure() {
    const image = this.#image;
    const slide = image.parentElement;
    this.#frame ??= {
      left: image.offsetLeft,
      top: image.offsetTop,
      width: image.offsetWidth,
      height: image.offsetHeight,
      slideWidth: slide.clientWidth,
      slideHeight: slide.clientHeight,
      rect: slide.getBoundingClientRect(),
    };
    return this.#frame;
  }

  // One zoom step: the original's resolution, between 2× and 3× (pinching goes further).
  #stepScale() {
    const original = Number(this.#image.getAttribute('width')) || 0;
    return Math.min(Math.max(original / this.#measure().width, ZOOM_STEP_MIN), ZOOM_STEP_MAX);
  }

  // Keeps a zoomed image over the whole slide (no gap at an edge), centred along a side it doesn't fill.
  #clamp(scale, x, y) {
    const frame = this.#measure();
    const fit = (offset, size, room, value) =>
      size * scale > room
        ? Math.min(-offset, Math.max(room - offset - size * scale, value))
        : (room - size * scale) / 2 - offset;
    return [fit(frame.left, frame.width, frame.slideWidth, x), fit(frame.top, frame.height, frame.slideHeight, y)];
  }

  #apply(scale, x, y, animate = false) {
    const image = this.#image;
    if (!image) return;
    [x, y] = this.#clamp(scale, x, y);
    this.#scale = scale;
    this.#x = x;
    this.#y = y;
    image.classList.toggle('is-animating', animate && !this.#reducedMotion.matches);
    image.style.transform = scale > 1 ? `translate(${x}px, ${y}px) scale(${scale})` : '';
    this.dialog.classList.toggle('is-zoomed', scale > 1);
  }

  // Zooms so the image point under (clientX, clientY) stays under it.
  #zoomAt(scale, clientX, clientY, animate) {
    const frame = this.#measure();
    const px = clientX - frame.rect.left;
    const py = clientY - frame.rect.top;
    const u = (px - frame.left - this.#x) / this.#scale;
    const v = (py - frame.top - this.#y) / this.#scale;
    this.#apply(scale, px - frame.left - u * scale, py - frame.top - v * scale, animate);
  }

  // With a mouse, the pointer's place over the slide picks the part of the image in view.
  #panTo(scale, clientX, clientY, animate) {
    const frame = this.#measure();
    const px = (clientX - frame.rect.left) / frame.slideWidth;
    const py = (clientY - frame.rect.top) / frame.slideHeight;
    this.#apply(
      scale,
      (frame.slideWidth - frame.width * scale) * px - frame.left,
      (frame.slideHeight - frame.height * scale) * py - frame.top,
      animate
    );
  }

  #reset(animate = false) {
    if (this.#image) this.#apply(1, 0, 0, animate);
    this.#scale = 1;
    this.#frame = null;
    this.#gesture = null;
  }

  // A zoomed image asks for a file sharp enough for its new size; srcset picks it, and the current one shows meanwhile.
  #sharpen() {
    const image = this.#image;
    if (!image || this.#scale <= 1) return;
    const width = this.#measure().width;
    const needed = Math.ceil(width * this.#scale);
    if (needed > (Number(image.dataset.sizedFor) || width) * 1.25) {
      image.sizes = `${needed}px`;
      image.dataset.sizedFor = String(needed);
    }
  }

  #onClick = (event) => {
    if (!event.target.closest('img')) {
      this.dialog.close();
      return;
    }
    if (this.#scale > 1) {
      this.#reset(true);
    } else {
      this.#panTo(this.#stepScale(), event.clientX, event.clientY, true);
      this.#sharpen();
    }
  };

  #onPointerMove = (event) => {
    if (event.pointerType !== 'mouse' || this.#scale <= 1) return;
    this.#panTo(this.#scale, event.clientX, event.clientY);
  };

  // Trackpad pinch (and ctrl + wheel); plain wheel and two-finger swipes still move between images.
  #onWheel = (event) => {
    if (!event.ctrlKey) return;
    event.preventDefault();
    const scale = Math.min(Math.max(this.#scale * Math.exp(-event.deltaY * 0.01), 1), ZOOM_MAX);
    if (scale === 1) {
      this.#reset();
      return;
    }
    this.#panTo(scale, event.clientX, event.clientY);
    this.#sharpen();
  };

  /* Touch: one finger swipes between images natively (or pans a zoomed one), two fingers pinch, and a
     double tap zooms in or out. Taps are answered here, so the browser's own double-tap zoom is off. */

  #onTouchStart = (event) => {
    const touches = [...event.touches];
    if (touches.length === 2) {
      event.preventDefault();
      if (!this.#image) return;
      this.#measure();
      const [a, b] = touches;
      this.#gesture = {
        type: 'pinch',
        distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        scale: this.#scale,
        midX: (a.clientX + b.clientX) / 2,
        midY: (a.clientY + b.clientY) / 2,
        x: this.#x,
        y: this.#y,
      };
    } else if (touches.length === 1) {
      this.#startPan(touches[0], event.timeStamp);
    }
  };

  #startPan(touch, time) {
    this.#gesture = {
      type: this.#scale > 1 ? 'pan' : 'swipe',
      startX: touch.clientX,
      startY: touch.clientY,
      x: this.#x,
      y: this.#y,
      moved: false,
      last: { x: touch.clientX, y: touch.clientY, time },
      velocity: { x: 0, y: 0 },
    };
  }

  #onTouchMove = (event) => {
    const gesture = this.#gesture;
    if (!gesture) return;
    const touches = [...event.touches];

    if (gesture.type === 'pinch') {
      event.preventDefault();
      if (touches.length !== 2) return;
      const [a, b] = touches;
      const frame = this.#measure();
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const scale = Math.min(Math.max((gesture.scale * distance) / gesture.distance, 1), ZOOM_MAX);
      // The image point that was under the fingers' midpoint follows it.
      const u = (gesture.midX - frame.rect.left - frame.left - gesture.x) / gesture.scale;
      const v = (gesture.midY - frame.rect.top - frame.top - gesture.y) / gesture.scale;
      const midX = (a.clientX + b.clientX) / 2 - frame.rect.left;
      const midY = (a.clientY + b.clientY) / 2 - frame.rect.top;
      this.#apply(scale, midX - frame.left - u * scale, midY - frame.top - v * scale);
      return;
    }

    const touch = touches[0];
    if (!touch) return;
    const dx = touch.clientX - gesture.startX;
    const dy = touch.clientY - gesture.startY;
    if (Math.hypot(dx, dy) > TAP_SLOP) gesture.moved = true;
    if (gesture.type !== 'pan') return;

    event.preventDefault();
    const elapsed = event.timeStamp - gesture.last.time;
    if (elapsed > 0) {
      gesture.velocity = {
        x: (touch.clientX - gesture.last.x) / elapsed,
        y: (touch.clientY - gesture.last.y) / elapsed,
      };
    }
    gesture.last = { x: touch.clientX, y: touch.clientY, time: event.timeStamp };
    this.#apply(this.#scale, gesture.x + dx, gesture.y + dy);
  };

  #onTouchEnd = (event) => {
    const gesture = this.#gesture;
    if (!gesture) return;

    if (gesture.type === 'pinch') {
      if (this.#scale < 1.1) this.#reset(true);
      else this.#sharpen();
      // A finger left on the screen carries on panning.
      if (event.touches.length === 1) this.#startPan(event.touches[0], event.timeStamp);
      else this.#gesture = null;
      return;
    }
    if (event.touches.length) return;
    this.#gesture = null;

    if (gesture.type === 'pan' && gesture.moved) {
      // A short glide in the direction of the flick.
      const { x, y } = gesture.velocity;
      if (Math.hypot(x, y) > 0.3) this.#apply(this.#scale, this.#x + x * 200, this.#y + y * 200, true);
      return;
    }
    if (!gesture.moved && event.type === 'touchend') this.#tap(event);
  };

  #tap(event) {
    const touch = event.changedTouches[0];
    // No click follows: the tap is answered here.
    if (event.cancelable) event.preventDefault();

    if (!event.target.closest('img')) {
      if (this.#scale === 1) this.dialog.close();
      return;
    }

    const last = this.#lastTap;
    const double =
      last &&
      event.timeStamp - last.time < DOUBLE_TAP_MS &&
      Math.hypot(touch.clientX - last.x, touch.clientY - last.y) < 40;
    this.#lastTap = double ? null : { time: event.timeStamp, x: touch.clientX, y: touch.clientY };
    if (!double) return;

    if (this.#scale > 1) {
      this.#reset(true);
    } else {
      this.#zoomAt(this.#stepScale(), touch.clientX, touch.clientY, true);
      this.#sharpen();
    }
  }
}

/*
 * <product-tabs> shows the information card's panels (snippets/product-details.liquid): as tabs on
 * wider screens (click, or arrow keys / Home / End, as in the ARIA tabs pattern), and on phones as an
 * accordion of the same panels (`[data-tab-trigger]` rows, any number open). Closed panels are
 * `hidden="until-found"`, so the browser's find-in-page opens the one holding a match
 * (`beforematch`). Switching between the two layouts keeps one panel open as tabs need.
 * "Read more" uncuts a long story.
 */
const PHONE_QUERY = window.matchMedia('(max-width: 749px)');

class ProductTabs extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.triggers = [...this.querySelectorAll('[data-tab-trigger]')];

    this.addEventListener(
      'click',
      (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab) {
          this.#holdHeight();
          this.#select(tab);
        }

        const trigger = event.target.closest('[data-tab-trigger]');
        if (trigger) this.#setOpen(trigger.getAttribute('aria-controls'), trigger.getAttribute('aria-expanded') !== 'true');

        const more = event.target.closest('[data-read-more]');
        if (more) this.#toggleStory(more);
      },
      { signal }
    );

    this.addEventListener(
      'keydown',
      (event) => {
        const index = this.tabs.indexOf(event.target);
        if (index === -1) return;
        const last = this.tabs.length - 1;
        const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: last }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        const tab = this.tabs[(next + this.tabs.length) % this.tabs.length];
        this.#holdHeight();
        this.#select(tab);
        tab.focus();
      },
      { signal }
    );

    this.addEventListener(
      'beforematch',
      (event) => {
        const panel = event.target.closest('[role="tabpanel"]');
        if (!panel) return;
        if (PHONE_QUERY.matches) {
          this.#setOpen(panel.id, true);
        } else {
          this.#holdHeight();
          this.#select(this.#tabFor(panel.id));
        }
      },
      { signal }
    );

    // Tabs show exactly one panel: coming from the phone accordion, keep the first open one.
    PHONE_QUERY.addEventListener(
      'change',
      (event) => {
        this.style.minHeight = '';
        if (event.matches) return;
        const open = this.tabs.find((tab) => !document.getElementById(tab.getAttribute('aria-controls'))?.hidden);
        this.#select(open ?? this.tabs[0]);
      },
      { signal }
    );

    // A new width reflows the panels, so the held height no longer applies.
    window.addEventListener('resize', () => (this.style.minHeight = ''), { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  // Tabs: the card never gets shorter than it first was (the description), so switching to a short
  // panel doesn't pull the page below it up, and back again doesn't push it down.
  #holdHeight() {
    if (!PHONE_QUERY.matches && !this.style.minHeight) this.style.minHeight = `${this.offsetHeight}px`;
  }

  #tabFor(panelId) {
    return this.tabs.find((tab) => tab.getAttribute('aria-controls') === panelId);
  }

  // Opens or closes one panel, keeping its tab and accordion row in step.
  #setOpen(panelId, open) {
    const panel = document.getElementById(panelId);
    if (!panel) return;
    if (open) panel.removeAttribute('hidden');
    else panel.setAttribute('hidden', 'until-found');

    const trigger = this.triggers.find((item) => item.getAttribute('aria-controls') === panelId);
    trigger?.setAttribute('aria-expanded', String(open));
    const tab = this.#tabFor(panelId);
    if (tab) {
      tab.setAttribute('aria-selected', String(open));
      tab.tabIndex = open ? 0 : -1;
    }
  }

  #select(selected) {
    if (!selected) return;
    for (const tab of this.tabs) this.#setOpen(tab.getAttribute('aria-controls'), tab === selected);
  }

  #toggleStory(button) {
    const body = document.getElementById(button.getAttribute('aria-controls'));
    const expanded = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;
    body?.classList.toggle('is-clamped', !expanded);
  }
}

/*
 * <sticky-buy> (snippets/sticky-buy.liquid) shows the buy bar at the bottom of the screen while the
 * buy row has scrolled up out of view (under the sticky header counts as out of view), and hides it
 * when the row is back or the footer comes into view, so it never covers the footer. One
 * IntersectionObserver over the row, the footer, and the page's sections; no scroll listener. The
 * bar is `hidden` until it first shows (its lazy image loads then); after that it slides with a
 * transform and is `inert` while away. Its image follows the variant's.
 */
class StickyBuy extends HTMLElement {
  #abort;
  #observer;

  connectedCallback() {
    const target = document.getElementById(this.dataset.target);
    if (!target) return;

    this.#abort = new AbortController();
    const footer = document.querySelector('.site-footer');

    // Where the row and the footer are, read when any watched element crosses a tenth of its height
    // (layout is fresh then). The row alone isn't enough: a jump or a fling can carry it from below
    // the screen to above it between two frames, and it never reports. The page's sections are tall,
    // so one of them always does.
    const update = () => {
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')) || 0;
      const pastRow = target.getBoundingClientRect().bottom <= header;
      const atFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      this.#toggle(pastRow && !atFooter);
    };
    this.#observer = new IntersectionObserver(update, { threshold: Array.from({ length: 11 }, (_, step) => step / 10) });
    for (const element of new Set([target, footer, ...document.querySelectorAll('main > *')])) {
      if (element) this.#observer.observe(element);
    }

    document.addEventListener('product:variant-change', () => this.#syncImage(), { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  #toggle(show) {
    if (show === this.classList.contains('is-visible')) return;
    this.inert = !show;
    if (!show) {
      this.classList.remove('is-visible');
      return;
    }
    this.hidden = false;
    // A frame in the hidden (translated) state first, so it slides in.
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-visible')));
  }

  // The variant's image, as a small file of the gallery's (Shopify's CDN sizes it from the URL).
  #syncImage() {
    const image = this.querySelector('.sticky-buy__media img');
    const mediaId = document.querySelector('[data-variant-state]')?.dataset.mediaId;
    const source = mediaId && document.querySelector(`.product-gallery__slide[data-media-id="${CSS.escape(mediaId)}"] img`);
    if (!image || !source) return;
    const url = new URL(source.currentSrc || source.src, window.location.href);
    url.searchParams.set('width', '160');
    url.searchParams.delete('height');
    image.removeAttribute('srcset');
    image.src = url.toString();
  }
}

if (!customElements.get('product-page')) customElements.define('product-page', ProductPage);
if (!customElements.get('sticky-buy')) customElements.define('sticky-buy', StickyBuy);
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
if (!customElements.get('product-zoom')) customElements.define('product-zoom', ProductZoom);
if (!customElements.get('product-tabs')) customElements.define('product-tabs', ProductTabs);
